import { Injectable } from '@angular/core';
import { SessionQuery } from '../../session/session.query';
import { FilterService } from '../../../interfaces/filter/filter-service';
import { TableColumn } from '../../../models/table-column';
import { ColumnSelectionModalService } from '../../../services/column-selection-modal.service';
import { TableColumnService } from '../../../interfaces/table/table-column.service';
import { tap } from 'rxjs/operators';
import { OperationBackendService } from '../../../services/operation/operation-backend.service';
import { applyTransaction } from '@datorama/akita';
import { LocalStateKeys } from '../../local-state-keys';
import { WorkInfoStoreService } from '../../work-info-store-service';
import { OperationItemsStoreService } from '../../operation-items-store-service';
import { OperationListService } from '../../operation-list/operation-list.service';
import { OperationListQuery } from '../../operation-list/operation-list.query';
import { LocalStoreConfigModel } from '../../../models/local-store-config.model';
import { OperationListParams } from 'src/app/core/models/client-models';

@Injectable({
  providedIn: 'root',
})
export class OperationItemsService
  implements FilterService, TableColumnService
{
  constructor(
    private sessionQuery: SessionQuery,
    private operationItemsStoreService: OperationItemsStoreService,
    private operationListService: OperationListService,
    private operationListQuery: OperationListQuery,
    private workInfoStoreService: WorkInfoStoreService,
    private columnSelectionModalService: ColumnSelectionModalService,
    private operationBackendService: OperationBackendService
  ) {}

  setFilterOptions(
    filterSelection: any,
    storeConfig?: LocalStoreConfigModel
  ): void {
    this.operationListService.setFilterOptions(filterSelection, null);
  }

  /**
   * Set new filter value.
   *
   * @param filterValue new filter value.
   * @param workInfoStoreKey
   * @param operationItemsKey
   */
  filterValueChange(
    filterValue: string,
    workInfoStoreKey: LocalStateKeys,
    operationItemsKey: LocalStateKeys
  ) {
    const store =
      this.operationItemsStoreService.getOrCreateStore(operationItemsKey);
    if (
      store.query.getAll().length == 0 &&
      filterValue.includes(this.operationListQuery.getValue()?.filterValue)
    ) {
      this.operationListService.filterValueChange(filterValue);
    } else {
      applyTransaction(() => {
        this.clearCache(operationItemsKey);
        this.operationListService.filterValueChange(filterValue);
      });
      this.getNextPage(workInfoStoreKey, operationItemsKey);
    }
  }

  /**
   * Set new Selected Columns.
   *
   * @param columns New selection of columns.
   */
  showSelectedColumns(columns: TableColumn[]): void {
    this.operationListService.showSelectedColumns(columns);
  }

  /**
   * Select next Operation list page.
   */
  getNextPage(
    workInfoStoreKey: LocalStateKeys,
    operationStoreKey: LocalStateKeys
  ) {
    const store =
      this.operationItemsStoreService.getOrCreateStore(operationStoreKey);
    store.store.setLoading(true);
    const nextPage: number = store.query.getValue().page + 1;
    this.setPage(nextPage, operationStoreKey);
    const pageSize: number = store.query.getValue().pageSize;
    const IdPerson: number = this.sessionQuery.getValue().user.id;
    const filterValue: string = this.operationListQuery.getValue().filterValue;

    const workInfoQuery =
      this.workInfoStoreService.getOrCreateStore(workInfoStoreKey);
    const orderID: number =
      workInfoQuery.query.getValue()?.workInfo?.order?.order?.orderID;

    const operationParams: OperationListParams = {
      pageNumber: nextPage,
      pageSize: pageSize,
      personID: IdPerson,
      orderID: orderID,
      filterValue: filterValue ? filterValue : '',
    } as OperationListParams;
    this.setFilter(operationParams);

    this.operationBackendService
      .getOperationList(operationParams)
      .pipe(tap(() => store.store.setLoading(false)))
      .subscribe({
        next: (data) => {
          store.store.upsertMany(data);
          store.store.setError(null);
          if (data.length == 0) {
            // if no results set page to previous.
            this.setPage(nextPage - 1, operationStoreKey);
            // set last page indicator.
            store.store.update((state) => (state.lastPage = true));
          }
        },
        error: (error) => store.store.setError(error.error),
      });
  }

  setPage(newPage: number, operationStoreKey: LocalStateKeys) {
    const store =
      this.operationItemsStoreService.getOrCreateStore(operationStoreKey);
    store.store.update((state) => ({
      ...state,
      page: newPage,
    }));
  }

  setFilter(params: OperationListParams) {
    return this.operationListService.setFilter(params);
  }

  /**
   * Set operation active in store.
   *
   * @param id Operation to set as active.
   * @param operationStoreKey
   */
  setActive(id: number, operationStoreKey: LocalStateKeys) {
    const store =
      this.operationItemsStoreService.getOrCreateStore(operationStoreKey);
    store.store.setActive(id);
  }

  /**
   * Initialize State.
   */
  clearCache(operationStoreKey: LocalStateKeys) {
    const store =
      this.operationItemsStoreService.getOrCreateStore(operationStoreKey);
    store.store.update((state) => ({
      ...state,
      page: 0,
      currentPage: 0,
      pageSize: 50,
      lastPage: false,
      textOperation: '',
    }));
    store.store.remove();
  }

  clearFilter() {
    this.operationListService.clearFilterValue();
  }
}
